/* istanbul ignore file */
import styled from "styled-components";
import Theme from "../../../styles/themes/interfaces/Theme";
import defaultTheme from "../../../styles/themes/default";
import { Paragraaf as ParagraafType } from "../../../.generated/rapportage/rapportagetypes";
import React, { ReactElement, Fragment } from "react";
import Markdown from "react-markdown";
import { h4TitleStyles } from "./Heading";

// Function to convert text with newlines to React elements with <br /> tags
export const renderTextWithLineBreaks = (text: string): ReactElement[] => {
  return text.split(/\r?\n/).map((line, index, array) => (
    <Fragment key={index}>
      {line}
      {index < array.length - 1 && <br />}
    </Fragment>
  ));
};

type ParagraphProps = {
  theme?: Theme;
  whiteSpace?: string;
  paragraaf?: ParagraafType;
  children?: React.ReactNode;
  className?: string;
  withMarkdown?: boolean;
  style?: string | React.CSSProperties;
  variant?: "bordered" | "accented";
  noMarginBottom?: boolean;
};

const paragraphStyles = ({
  theme,
  whiteSpace = "normal",
  style,
  variant,
  noMarginBottom,
}: ParagraphProps): string => {
  theme = theme ? theme : defaultTheme;

  const styleString =
    typeof style === "string"
      ? style
      : style
      ? Object.entries(style)
          .map(([key, value]) => `${key}: ${value}`)
          .join("; ")
      : "";

  return `
    font-family: ${theme.fontFace};
    white-space: ${whiteSpace};
    color: ${theme.fg?.primary};
    font-size: ${theme.text.fontSize};
    line-height: ${theme.text?.lineHeight};
    margin: 0;
    margin-top: ${theme.spacing?.m};
    ${!noMarginBottom ? `margin-bottom: ${theme.spacing?.xxl};` : ""}
    margin-left: ${theme.ptom ? theme.ptom(8) : "0.16mm"};
    ${styleString};
    ${variant === "bordered" &&
      `
      border: 1px solid ${theme.fg?.primary};
      border-radius: 4px;
      padding: ${theme.ptom ? theme.ptom(8) : "0.16mm"};
    `};
    ${variant === "accented" &&
      `
      background-color: #f5f5f5;
      padding: ${theme.ptom ? `${theme.ptom(16)} ${theme.ptom(12)}` : "0.16mm"};
      border-radius: 4px;
    `};
  `;
};

const StyledParagraph = styled.p<ParagraphProps>(paragraphStyles);
const ParagraphTitle = styled.p(
  ({ theme }) => `
  ${h4TitleStyles(theme)}
  font-weight: bold;
  margin-left: ${theme.ptom(8)};
  margin-bottom: ${theme.spacing?.l};

`
);

const StyledMarkdown = styled(Markdown)<ParagraphProps>(paragraphStyles);

export const Paragraph = ({
  paragraaf,
  children,
  withMarkdown = true,
  ...otherProps
}: ParagraphProps): ReactElement => {
  // If paragraaf is provided, render according to that structure
  if (paragraaf) {
    return (
      <>
        {paragraaf.paragraafNaam && (
          <ParagraphTitle>{paragraaf.paragraafNaam}</ParagraphTitle>
        )}
        {paragraaf.inhoud &&
          (withMarkdown ? (
            <StyledMarkdown className="chapter__paragraph" {...otherProps}>
              {paragraaf.inhoud}
            </StyledMarkdown>
          ) : (
            <StyledParagraph className="chapter__paragraph" {...otherProps}>
              {renderTextWithLineBreaks(paragraaf.inhoud)}
            </StyledParagraph>
          ))}
      </>
    );
  }

  // Otherwise, just render as a styled paragraph with children
  return withMarkdown && typeof children === "string" ? (
    <StyledMarkdown className="chapter__paragraph" {...otherProps}>
      {children}
    </StyledMarkdown>
  ) : (
    <StyledParagraph className="chapter__paragraph" {...otherProps}>
      {children}
    </StyledParagraph>
  );
};

// Export the old name for backward compatibility
